/**
 * Shared section components for Market Insights day-detail modals.
 * Used by both admin MarketInsightsPanel and user UserMarketPerformanceView.
 */

import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type {
  MarketInsightAI,
  MarketInsightBiasAssessment,
  MarketInsightPlayerStats,
  MarketInsightPropTypeStat,
  MarketInsightSituationalStats,
  PlayerStat,
  SituationalStat,
} from '@views/lab/hooks/useLabDashboard';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { getHitRateColor } from './marketInsightsHelpers';

// =============================================================================
// Situational Factors
// =============================================================================

function SituationalTable({
  title,
  data,
  showParlayStats = true,
}: {
  title: string;
  data: SituationalStat[];
  showParlayStats?: boolean;
}): ReactElement | null {
  if (!data || data.length === 0) return null;

  return (
    <div className="mb-3">
      <h5 className="text-[10px] font-semibold text-theme-text-tertiary uppercase mb-1">{title}</h5>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-theme-text-tertiary text-[10px] uppercase">
            <th className="text-left pb-1">Factor</th>
            <th className="text-right pb-1">Leg %</th>
            {showParlayStats && (
              <>
                <th className="text-right pb-1">Parlay %</th>
                <th className="text-right pb-1">Units</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((stat) => (
            <tr key={stat.label} className="border-t border-theme-border/30">
              <td className="py-1 text-theme-text-secondary">{stat.label}</td>
              <td className={`py-1 text-right ${getHitRateColor(stat.legHitRate)}`}>
                {stat.legTotal > 0 ? `${stat.legHitRate}%` : '-'}
                <span className="text-gray-600 ml-1">({stat.legTotal})</span>
              </td>
              {showParlayStats && (
                <>
                  <td className={`py-1 text-right ${getHitRateColor(stat.parlayHitRate)}`}>
                    {stat.parlayTotal > 0 ? `${stat.parlayHitRate}%` : '-'}
                    <span className="text-gray-600 ml-1">({stat.parlayTotal})</span>
                  </td>
                  <td
                    className={`py-1 text-right ${stat.parlayUnits >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                  >
                    {stat.parlayTotal > 0
                      ? `${stat.parlayUnits >= 0 ? '+' : ''}${stat.parlayUnits.toFixed(1)}u`
                      : '-'}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SituationalFactorsSection({
  stats,
}: {
  stats: MarketInsightSituationalStats;
}): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-theme-surface/30 rounded-lg p-3">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-xs font-semibold text-theme-text-secondary uppercase"
      >
        <span>Situational Factors</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="mt-3 grid grid-cols-1 @3xl:grid-cols-2 gap-4">
          <SituationalTable title="Day of Week" data={stats.dayOfWeek} showParlayStats={false} />
          <SituationalTable title="Home / Away" data={stats.homeAway} />
          <SituationalTable title="Spread Range" data={stats.spreadRange} />
          <SituationalTable title="B2B Status" data={stats.b2bStatus} />
          <SituationalTable title="Blowout Risk" data={stats.blowoutRisk} showParlayStats={false} />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Player Performance
// =============================================================================

function PlayerTable({
  title,
  players,
  colorClass,
}: {
  title: string;
  players: PlayerStat[];
  colorClass: string;
}): ReactElement | null {
  if (!players || players.length === 0) return null;

  return (
    <div>
      <h5 className={`text-[10px] font-semibold uppercase mb-1 ${colorClass}`}>{title}</h5>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-theme-text-tertiary text-[10px] uppercase">
            <th className="text-left pb-1">Player</th>
            <th className="text-left pb-1">Team</th>
            <th className="text-right pb-1">Picks</th>
            <th className="text-right pb-1">Hit %</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={`${p.player}-${p.team}`} className="border-t border-theme-border/30">
              <td className="py-1 text-theme-text-secondary truncate max-w-[120px]">{p.player}</td>
              <td className="py-1 text-theme-text-tertiary">{p.team}</td>
              <td className="py-1 text-right text-theme-text-secondary">{p.total}</td>
              <td className={`py-1 text-right ${getHitRateColor(p.hitRate)}`}>{p.hitRate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PlayerPerformanceSection({
  stats,
}: {
  stats: MarketInsightPlayerStats;
}): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-theme-surface/30 rounded-lg p-3">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-xs font-semibold text-theme-text-secondary uppercase"
      >
        <span>Player Performance</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="mt-3 grid grid-cols-1 @3xl:grid-cols-2 gap-4">
          <PlayerTable
            title="Hot Players"
            players={stats.hotPlayers}
            colorClass="text-theme-success"
          />
          <PlayerTable
            title="Cold Players"
            players={stats.coldPlayers}
            colorClass="text-theme-danger"
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Prop Type Breakdown
// =============================================================================

export function PropTypeBreakdownSection({
  stats,
}: {
  stats: MarketInsightPropTypeStat[];
}): ReactElement | null {
  if (!stats || stats.length === 0) return null;

  return (
    <div className="bg-theme-surface/30 rounded-lg p-3">
      <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
        By Prop Type
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-theme-text-tertiary text-[10px] uppercase">
              <th className="text-left pb-2">Prop</th>
              <th className="text-right pb-2">Total</th>
              <th className="text-right pb-2">Hit %</th>
              <th className="text-right pb-2">Over %</th>
              <th className="text-right pb-2">Under %</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((pt) => (
              <tr key={pt.propType} className="border-t border-theme-border/50">
                <td className="py-2 text-gray-200">{formatStatLabel(pt.propType)}</td>
                <td className="py-2 text-right text-theme-text-secondary">{pt.total}</td>
                <td className={`py-2 text-right ${getHitRateColor(pt.hitRate)}`}>{pt.hitRate}%</td>
                <td className={`py-2 text-right ${getHitRateColor(pt.overHitRate)}`}>
                  {pt.overCount > 0 ? `${pt.overHitRate}%` : '-'}
                </td>
                <td className={`py-2 text-right ${getHitRateColor(pt.underHitRate)}`}>
                  {pt.underCount > 0 ? `${pt.underHitRate}%` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Bias Assessment
// =============================================================================

export function BiasAssessmentSection({
  bias,
}: {
  bias: MarketInsightBiasAssessment;
}): ReactElement {
  return (
    <div className="bg-theme-surface/30 rounded-lg p-3">
      <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-2">
        Model Balance
      </h4>
      <div className="flex items-center gap-3">
        <span className="text-xl">{bias.emoji}</span>
        <div>
          <div className="text-sm text-gray-200">
            {bias.overBiasPct}% Over / {100 - bias.overBiasPct}% Under
          </div>
          <div
            className={`text-xs ${bias.isBalanced ? 'text-theme-success' : 'text-theme-warning'}`}
          >
            {bias.isBalanced
              ? 'Balanced distribution'
              : `Biased toward ${bias.overBiasPct > 50 ? 'Overs' : 'Unders'}`}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// AI Insights
// =============================================================================

export function AIInsightsSection({ ai }: { ai: MarketInsightAI }): ReactElement | null {
  if (!ai.explanation) return null;

  return (
    <div className="bg-theme-primary/10 border border-theme-primary/30 rounded-lg p-3">
      <h4 className="text-xs font-semibold text-theme-primary uppercase mb-2">AI Analysis</h4>
      <p className="text-sm text-theme-text-secondary mb-2">{ai.explanation}</p>
      {ai.recommendation && (
        <p className="text-xs text-theme-primary italic mb-2">
          Recommendation: {ai.recommendation}
        </p>
      )}
      {ai.keyFactors && ai.keyFactors.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {ai.keyFactors.map((factor, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-theme-primary/20 text-theme-primary text-xs rounded"
            >
              {factor}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
